// @flow
import {ActionsObservable, ofType} from 'redux-observable';
import {debounceTime, map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {
  createCheckoutInputApi,
  deleteCheckoutInputApi,
  editCheckoutInputApi,
  endpoints,
  getCheckoutInputApi,
  getOrderStateMessagesApi,
  getSalesProcessAdditionalInformationApi,
  sortCheckoutInputApi,
  updateOrderStateMessagesApi,
} from './api';

import {buildRoute} from '../../../http';

import {GetSessionSuccess, LoginSuccess} from '../../auth/duck';

import type {
  SalesProcessInformation,
  OrderStateMessageConfig,
  CheckoutInput,
} from '../../../entities';

import type {Action} from '../../../store/utils';

export type SalesProcessAction = Action & {
  identificationNumber?: boolean,
  id?: string,
};

export type SalesProcessConfigState = {
  isFetchingSalesProcessInformation?: boolean,
  isSaving?: boolean,
  error?: string,
  salesProcessInformation?: SalesProcessInformation,
  orderStateMessages?: OrderStateMessageConfig[],
  isFetchingOrderStateMessages?: boolean,
  updateSuccess?: boolean,
  saveCheckoutInputSuccess?: boolean,
  isFetchingInput?: boolean,
  fetchInputError?: string,
  saveInputError?: string,
  checkoutInput?: CheckoutInput,
};

export class UpdateSalesProcessInformation extends ActionCreator<SalesProcessAction> {}
export class UpdateSalesProcessInformationSuccess extends ActionCreator<SalesProcessAction> {}
export class UpdateSalesProcessInformationErrorFn extends ActionCreator<SalesProcessAction> {}

export class GetSalesProcessInformation extends ActionCreator<SalesProcessAction> {}
export class GetSalesProcessInformationSuccess extends ActionCreator<SalesProcessAction> {}
export class GetSalesProcessInformationErrorFn extends ActionCreator<SalesProcessAction> {}

export class FetchCheckoutInput extends ActionCreator<SalesProcessAction> {}
export class FetchCheckoutInputSuccess extends ActionCreator<SalesProcessAction> {}
export class FetchCheckoutInputErrorFn extends ActionCreator<SalesProcessAction> {}

export class CreateCheckoutInput extends ActionCreator<SalesProcessAction> {}
export class CreateCheckoutInputSuccess extends ActionCreator<SalesProcessAction> {}
export class CreateCheckoutInputErrorFn extends ActionCreator<SalesProcessAction> {}

export class EditCheckoutInput extends ActionCreator<SalesProcessAction> {}
export class EditCheckoutInputSuccess extends ActionCreator<SalesProcessAction> {}
export class EditCheckoutInputErrorFn extends ActionCreator<SalesProcessAction> {}

export class DeleteCheckoutInput extends ActionCreator<SalesProcessAction> {}
export class DeleteCheckoutInputSuccess extends ActionCreator<SalesProcessAction> {}
export class DeleteCheckoutInputErrorFn extends ActionCreator<SalesProcessAction> {}

export class SortCheckoutInput extends ActionCreator<SalesProcessAction> {}
export class SortCheckoutInputSuccess extends ActionCreator<SalesProcessAction> {}
export class SortCheckoutInputErrorFn extends ActionCreator<SalesProcessAction> {}

export class UpdateOrderStateMessages extends ActionCreator<SalesProcessAction> {}
export class UpdateOrderStateMessagesSuccess extends ActionCreator<SalesProcessAction> {}
export class UpdateOrderStateMessagesErrorFn extends ActionCreator<SalesProcessAction> {}

export class GetOrderStateMessages extends ActionCreator<SalesProcessAction> {}
export class GetOrderStateMessagesSuccess extends ActionCreator<SalesProcessAction> {}
export class GetOrderStateMessagesErrorFn extends ActionCreator<SalesProcessAction> {}
export class ResetState extends ActionCreator<SalesProcessAction> {}
export class ResetCheckoutPage extends ActionCreator<SalesProcessAction> {}

export const actions = {
  updateSalesProcessInformation: (payload: SalesProcessAction) =>
    new UpdateSalesProcessInformation(payload),
  getSalesProcessInformation: () => new GetSalesProcessInformation(),
  updateOrderStateMessages: (payload: SalesProcessAction) =>
    new UpdateOrderStateMessages(payload),
  getOrderStateMessages: () => new GetOrderStateMessages(),
  resetState: () => new ResetState(),
  //custom inputs
  fetchCheckoutInput: (payload: SalesProcessAction) =>
    new FetchCheckoutInput(payload),
  createCheckoutInput: (payload: SalesProcessAction) =>
    new CreateCheckoutInput(payload),
  editCheckoutInput: (payload: SalesProcessAction) =>
    new EditCheckoutInput(payload),
  deleteCheckoutInput: (payload: SalesProcessAction) =>
    new DeleteCheckoutInput(payload),
  sortCheckoutInput: (payload: SalesProcessAction) =>
    new SortCheckoutInput(payload),
  resetCheckoutPage: () => new ResetCheckoutPage(),
};

export const initialState = {};

export default createReducer<SalesProcessConfigState>(initialState, {
  [UpdateSalesProcessInformation.type]: (state: SalesProcessConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateSalesProcessInformationSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    error: undefined,
    salesProcessInformation: action.data,
    updateSuccess: true,
    isSaving: false,
  }),
  [UpdateSalesProcessInformationErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: false,
    };
  },
  [GetSalesProcessInformation.type]: (state: SalesProcessConfigState) => ({
    ...state,
    salesProcessInformation: undefined,
    error: undefined,
    isFetchingSalesProcessInformation: true,
  }),
  [GetSalesProcessInformationSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    salesProcessInformation: action.data,
    isFetchingSalesProcessInformation: false,
  }),
  [GetSalesProcessInformationErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => {
    return {
      ...state,
      salesProcessInformation: undefined,
      error: 'Error al obtener la configuración de procesos de ventas',
      isFetchingSalesProcessInformation: false,
    };
  },
  [UpdateOrderStateMessages.type]: (state: SalesProcessConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateOrderStateMessagesSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => {
    const newOrderStateMessages = [...(state.orderStateMessages || [])];
    const index = newOrderStateMessages.findIndex(
      m => m.orderState === action.data.orderState
    );
    if (index > -1) {
      newOrderStateMessages[index] = action.data;
    } else newOrderStateMessages.push(action.data);
    return {
      ...state,
      error: undefined,
      orderStateMessages: newOrderStateMessages,
      updateSuccess: true,
      isSaving: false,
    };
  },
  [UpdateOrderStateMessagesErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    error: action.error,
    updateSuccess: false,
    isSaving: false,
  }),
  [GetOrderStateMessages.type]: (state: SalesProcessConfigState) => ({
    ...state,
    orderStateMessages: undefined,
    error: undefined,
    isFetchingOrderStateMessages: true,
  }),
  [GetOrderStateMessagesSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    orderStateMessages: action.data,
    isFetchingOrderStateMessages: false,
  }),
  [GetOrderStateMessagesErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => {
    return {
      ...state,
      orderStateMessages: undefined,
      error: 'Error al obtener los mensajes configurados',
      isFetchingOrderStateMessages: false,
    };
  },
  [FetchCheckoutInput.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isFetchingInput: true,
    checkoutInput: undefined,
    fetchInputError: undefined,
  }),
  [FetchCheckoutInputSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isFetchingInput: undefined,
    checkoutInput: action.data,
    fetchInputError: undefined,
  }),
  [FetchCheckoutInputErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isFetchingInput: undefined,
    checkoutInput: undefined,
    fetchInputError: 'Error al obtener data.',
  }),
  [CreateCheckoutInput.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: true,
    saveCheckoutInputSuccess: undefined,
    saveInputError: undefined,
  }),
  [CreateCheckoutInputSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: true,
    saveInputError: undefined,
  }),
  [CreateCheckoutInputErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: undefined,
    saveInputError: 'Error al crear el campo.',
  }),

  [EditCheckoutInput.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: true,
    saveCheckoutInputSuccess: undefined,
    saveInputError: undefined,
  }),
  [EditCheckoutInputSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: true,
    saveInputError: undefined,
  }),
  [EditCheckoutInputErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: undefined,
    saveInputError: 'Error al editar el campo',
  }),
  [DeleteCheckoutInput.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: true,
    saveCheckoutInputSuccess: undefined,
    saveInputError: undefined,
  }),
  [DeleteCheckoutInputSuccess.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: true,
    saveInputError: undefined,
  }),
  [DeleteCheckoutInputErrorFn.type]: (
    state: SalesProcessConfigState,
    action: SalesProcessAction
  ) => ({
    ...state,
    isSaving: false,
    saveCheckoutInputSuccess: undefined,
    saveInputError: 'Error al eliminar el campo.',
  }),
  [ResetCheckoutPage.type]: (state: SalesProcessConfigState) => ({
    ...state,
    saveCheckoutInputSuccess: undefined,
    isSaving: undefined,
    checkoutInput: undefined,
    fetchInputError: undefined,
    isFetchingInput: undefined,
    saveInputError: undefined,
  }),
  [ResetState.type]: ({salesProcessInformation}: SalesProcessConfigState) => ({
    salesProcessInformation,
  }),
});

export const getSalesProcessInformationEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(
      LoginSuccess.type,
      GetSessionSuccess.type,
      GetSalesProcessInformation.type,
      CreateCheckoutInputSuccess.type,
      DeleteCheckoutInputSuccess.type,
      EditCheckoutInputSuccess.type,
      SortCheckoutInputSuccess.type
    ),
    switchMap(() =>
      getSalesProcessAdditionalInformationApi().pipe(
        map(data => new GetSalesProcessInformationSuccess({data: data})),
        buildCatchError(GetSalesProcessInformationErrorFn)
      )
    )
  );

// export const updateSalesProcessInformationEpic = (action$: ActionsObservable) =>
//   action$.pipe(
//     ofType(UpdateSalesProcessInformation.type),
//     switchMap(({request}) => {
//       return updateSalesProcessInformationApi(request).pipe(
//         map(data => new UpdateSalesProcessInformationSuccess({data: data})),
//         buildCatchError(UpdateSalesProcessInformationErrorFn)
//       );
//     })
//   );

export const getOrderStateMessagesEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(GetOrderStateMessages.type),
    switchMap(() =>
      getOrderStateMessagesApi().pipe(
        map(data => new GetOrderStateMessagesSuccess({data: data})),
        buildCatchError(GetOrderStateMessagesErrorFn)
      )
    )
  );

export const updateOrderStateMessagesEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateOrderStateMessages.type),
    switchMap(({request}) => {
      return updateOrderStateMessagesApi(request).pipe(
        map(data => new UpdateOrderStateMessagesSuccess({data})),
        buildCatchError(UpdateOrderStateMessagesErrorFn)
      );
    })
  );

//Custom inputs

export const getCheckoutInputEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchCheckoutInput.type),
    switchMap(({checkoutInputRequest}) => {
      return getCheckoutInputApi(undefined, {
        route: buildRoute(endpoints.GET_CHECKOUT_INPUT, [
          checkoutInputRequest.id,
        ]),
      }).pipe(
        map((data: CheckoutInput) => new FetchCheckoutInputSuccess({data})),
        buildCatchError(FetchCheckoutInputErrorFn)
      );
    })
  );

export const createCheckoutInputEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(CreateCheckoutInput.type),
    switchMap(({checkoutInputRequest}) => {
      return createCheckoutInputApi(checkoutInputRequest).pipe(
        map(_ => new CreateCheckoutInputSuccess()),
        buildCatchError(CreateCheckoutInputErrorFn)
      );
    })
  );

export const editCheckoutInputEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(EditCheckoutInput.type),
    debounceTime(500),
    switchMap(({checkoutInputRequest}) => {
      const {id, ...rest} = checkoutInputRequest;
      return editCheckoutInputApi(rest, {
        route: buildRoute(endpoints.GET_CHECKOUT_INPUT, [id]),
      }).pipe(
        map(_ => new EditCheckoutInputSuccess()),
        buildCatchError(EditCheckoutInputErrorFn)
      );
    })
  );

export const deleteCheckoutInputEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(DeleteCheckoutInput.type),
    switchMap(({checkoutInputRequest}) => {
      return deleteCheckoutInputApi({
        route: buildRoute(endpoints.GET_CHECKOUT_INPUT, [
          checkoutInputRequest.id,
        ]),
      }).pipe(
        map(_ => new DeleteCheckoutInputSuccess()),
        buildCatchError(DeleteCheckoutInputErrorFn)
      );
    })
  );

export function sortCheckoutInputEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SortCheckoutInput.type),
    switchMap(({sortRequest}: SalesProcessAction) => {
      const {init, end} = sortRequest;
      return sortCheckoutInputApi({
        originStoreOrderAdditionalInformationId: init.id,
        targetStoreOrderAdditionalInformationId: end.id,
      }).pipe(
        map(data => new SortCheckoutInputSuccess()),
        buildCatchError(SortCheckoutInputErrorFn, 'Error al ordenar el campo')
      );
    })
  );
}
